/**
 * Miscellaneous tracking functions.
 * Handles VAT, Pop-up, and experiment tracking events.
 */

import { TRACKING_EVENTS, TRACKING_CATEGORIES, NAV_FLYOUT_EXPERIMENT } from '../constants/analytics'
import { Locale } from '../types/localization'
import { getTrackingConfig, safeTrack } from './config'

/**
 * Tracks a VAT option selection event.
 * Page tracking config is automatically retrieved from meta tags.
 *
 * @param label - The VAT option label
 */
export const trackVatOption = (label: string): void => {
  const trackingConfig = getTrackingConfig()

  safeTrack(TRACKING_EVENTS.VAT_OPTION_SELECTED, {
    ...trackingConfig,
    category: TRACKING_CATEGORIES.VAT,
    label,
  })
}

/**
 * Tracks a navigation hover event (currently only for de-de locale).
 *
 * @param analyticsId - The analytics ID of the hovered element
 * @param locale - The current locale
 */
export const trackNavHover = (analyticsId: string, locale: string): void => {
  if (locale.toLowerCase() === Locale.deDE) {
    safeTrack(TRACKING_EVENTS.EXPERIMENT_VIEWED, {
      experimentName: NAV_FLYOUT_EXPERIMENT,
      analyticsId,
    })
  }
}

/**
 * Tracks a pop-up interaction event.
 * Page tracking config is automatically retrieved from meta tags.
 *
 * @param action - The action/event name
 * @param label - The event label
 * @param eventDetail - Optional event detail
 */
export const trackPopInteraction = (action: string, label: string, eventDetail?: string): void => {
  const trackingConfig = getTrackingConfig()

  safeTrack(action, {
    ...trackingConfig,
    category: TRACKING_CATEGORIES.POP_UP,
    label,
    eventDetail,
  })
}

/**
 * Resets the tracking state (typically called on sign-out).
 */
export const resetTracking = (): void => {
  if (window.tracking?.reset) {
    window.tracking.reset()
  }
}
